import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth, temPapel } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { mensagemDeErro, rpc } from "../lib/rpc";
import { fmtDataHora, fmtNum, nomeTipoLocal } from "../lib/formato";
import { Botao, MensagemErro, Selo, Tabela, Vazio } from "./ui";

interface Saldo { quantidade: number; reservado: number; produtos: { codigo: string | null; nome: string } }
interface Movimento {
  id: number; criado_em: string; tipo: string; quantidade: number; motivo: string | null;
  local_origem_id: string | null; local_destino_id: string | null;
  produtos: { codigo: string | null } | null;
  origem: { codigo: string } | null; destino: { codigo: string } | null;
}

/** Conteúdo de uma posição: o que tem, quanto está reservado e o que mexeu nela por último. */
export function DetalhePosicao({ localId, codigo, onFechar }: { localId: string; codigo: string; onFechar: () => void }) {
  const { perfil } = useAuth();
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const local = useQuery({
    queryKey: ["posicao", localId, "local"],
    queryFn: async () => {
      const { data, error } = await supabase.from("locais").select("codigo, tipo, rua, modulo, nivel, capacidade, bloqueado, ordem_rota")
        .eq("id", localId).maybeSingle();
      if (error) throw error;
      return data as { codigo: string; tipo: string; rua: string | null; modulo: string | null; nivel: string | null;
                       capacidade: number | null; bloqueado: boolean; ordem_rota: number | null } | null;
    },
  });
  const saldos = useQuery({
    queryKey: ["posicao", localId, "saldos"],
    queryFn: async () => {
      const { data, error } = await supabase.from("saldo_posicao").select("quantidade, reservado, produtos!inner(codigo, nome)")
        .eq("local_id", localId).gt("quantidade", 0).order("quantidade", { ascending: false });
      if (error) throw error;
      return data as unknown as Saldo[];
    },
  });
  const movimentos = useQuery({
    queryKey: ["posicao", localId, "movimentos"],
    queryFn: async () => {
      const { data, error } = await supabase.from("movimentacoes_estoque")
        .select("id, criado_em, tipo, quantidade, motivo, local_origem_id, local_destino_id, produtos(codigo), origem:locais!movimentacoes_estoque_local_origem_id_fkey(codigo), destino:locais!movimentacoes_estoque_local_destino_id_fkey(codigo)")
        .or(`local_origem_id.eq.${localId},local_destino_id.eq.${localId}`)
        .order("criado_em", { ascending: false }).limit(15);
      if (error) throw error;
      return data as unknown as Movimento[];
    },
  });

  async function pedirContagem() {
    setOcupado(true);
    setErro(null);
    setAviso(null);
    try {
      const r = await rpc("wms_inventario_criar", { p_tipo: "POSICAO", p_locais: [codigo], p_bloquear: false, p_descricao: `Contagem da posição ${codigo}` });
      if (!r.ok) setErro(r.mensagem ?? "Não foi possível pedir a contagem");
      else setAviso("Contagem pedida: aparece na coletora do estoquista");
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  const l = local.data;
  const total = (saldos.data ?? []).reduce((s, x) => s + Number(x.quantidade), 0);

  return (
    <div data-dialogo-aberto className="fixed inset-0 z-40 flex items-end justify-center bg-tinta/60 p-2 sm:items-center sm:p-4"
      role="dialog" aria-modal="true" aria-label={`Posição ${codigo}`} onClick={onFechar}>
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col gap-3 overflow-y-auto rounded-lg bg-chapa p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="font-sinal text-3xl font-bold">{codigo}</h2>
            {l && (
              <p className="text-aco-escuro">
                {nomeTipoLocal[l.tipo] ?? l.tipo}
                {l.rua && ` · rua ${l.rua}`}{l.modulo && ` · prédio ${l.modulo}`}{l.nivel && ` · nível ${l.nivel}`}
                {l.capacidade ? ` · capacidade ${fmtNum(l.capacidade)}` : ""}
                {l.ordem_rota != null && ` · rota ${l.ordem_rota}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {l?.bloqueado && <Selo tom="alerta">Bloqueada</Selo>}
            <Botao variante="fantasma" onClick={onFechar}>Fechar</Botao>
          </div>
        </div>

        <section>
          <h3 className="mb-1 font-sinal text-xl font-semibold">Conteúdo ({fmtNum(total)} un)</h3>
          {saldos.data?.length ? (
            <Tabela cabecalho={["SKU", "Produto", "Saldo", "Reservado", "Disponível"]}>
              {saldos.data.map((s, i) => (
                <tr key={i}>
                  <td className="font-semibold">{s.produtos.codigo}</td>
                  <td>{s.produtos.nome}</td>
                  <td className="font-sinal text-lg font-bold">{fmtNum(s.quantidade)}</td>
                  <td>{fmtNum(s.reservado)}</td>
                  <td>{fmtNum(Number(s.quantidade) - Number(s.reservado))}</td>
                </tr>
              ))}
            </Tabela>
          ) : <Vazio>{saldos.isLoading ? "Carregando..." : "Posição vazia."}</Vazio>}
        </section>

        <section>
          <h3 className="mb-1 font-sinal text-xl font-semibold">Últimas movimentações</h3>
          {movimentos.data?.length ? (
            <Tabela cabecalho={["Quando", "Tipo", "SKU", "Qtd", "De", "Para"]}>
              {movimentos.data.map((m) => {
                const entrou = m.local_destino_id === localId;
                return (
                  <tr key={m.id} title={m.motivo ?? undefined}>
                    <td className="whitespace-nowrap text-sm">{fmtDataHora(m.criado_em)}</td>
                    <td className="text-sm">{m.tipo.replaceAll("_", " ").toLowerCase()}</td>
                    <td>{m.produtos?.codigo}</td>
                    <td className={`font-semibold ${entrou ? "text-ok" : "text-erro"}`}>{entrou ? "+" : "-"}{fmtNum(m.quantidade)}</td>
                    <td className="text-sm">{m.origem?.codigo ?? "entrada"}</td>
                    <td className="text-sm">{m.destino?.codigo ?? "saída"}</td>
                  </tr>
                );
              })}
            </Tabela>
          ) : <Vazio>{movimentos.isLoading ? "Carregando..." : movimentos.isError ? "Seu usuário não vê o histórico." : "Nenhuma movimentação."}</Vazio>}
        </section>

        <div className="flex flex-wrap gap-2">
          {total > 0 && (
            <Link to={`/estoque/transferir?origem=${encodeURIComponent(codigo)}`}
              className="inline-flex min-h-12 items-center rounded-md border-2 border-tinta px-4 font-semibold">
              Mover daqui
            </Link>
          )}
          {temPapel(perfil, "lider") && (
            <Botao variante="secundario" disabled={ocupado} onClick={() => void pedirContagem()}>Pedir contagem desta posição</Botao>
          )}
        </div>
        {aviso && <p className="rounded-md bg-ok-claro px-3 py-2 font-semibold text-ok" role="status">{aviso}</p>}
        <MensagemErro>{erro}</MensagemErro>
      </div>
    </div>
  );
}
